var __extends = (this && this.__extends) || (function () {
    var extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Injectable } from '@angular/core';
import { CanvasProvider } from './workspace/canvas';
import { TaggingTool } from './workspace/tagging-tool.enum';
import { BaseProvider } from './base';
import { EventName, Events } from '../utils/events';
import { ActiveToolProvider } from './workspace/active-tool';
import { TranslateService } from '@ngx-translate/core';
import { DatasetProvider } from './dataset';
export var WorkspaceRootPage;
(function (WorkspaceRootPage) {
    WorkspaceRootPage[WorkspaceRootPage["WORKSPACE"] = 0] = "WORKSPACE";
    WorkspaceRootPage[WorkspaceRootPage["EVALUATE_BRAIN"] = 1] = "EVALUATE_BRAIN";
})(WorkspaceRootPage || (WorkspaceRootPage = {}));
var WorkspaceProvider = /** @class */ (function (_super) {
    __extends(WorkspaceProvider, _super);
    function WorkspaceProvider(canvasProvider, activeToolProvider, events, datasetProvider, translate) {
        var _this = _super.call(this) || this;
        _this.canvasProvider = canvasProvider;
        _this.activeToolProvider = activeToolProvider;
        _this.events = events;
        _this.datasetProvider = datasetProvider;
        _this.translate = translate;
        /* hotKeyDefinitions is a lookup table (nested JSON object) that performs the second part of our
         * keyboard input -> function call translation. By the time we look up a value from here,
         * we have already converted the latest keyboard input into a standardized hash. We then use the current
         * workspace type and mode to narrow down our search, and then check to see if there's a value (a tool name)
         * that corresponds to this hash. If there is, we then search for the key in toolDefinitions and execute
         * any function tied to that tool key. Otherwise, if the user's input hash doesn't match any of these tools,
         * we just ignore it.
         */
        _this.hotKeyDefinitions = {
            /* NOTES:
             * The lowest level of hotkey definitions (actual hotkey hashes) should be sorted alphabetically.
             * findTool() translates 'ctrlKey' events into 'metaKey', so please only use the 'metaKey' modifier and not 'ctrlKey'.
             * If we introduce localization or rebinding, we may need to create multiple instances of this object
             * and spin them off into their own file(s).
             * By using function definitions and a check in findTool(), we can add extra logic to hash parsing as needed.
             */
            'Scene Recognition': {
                Workspace: {
                    ArrowDown: 'Nudge View Down',
                    ArrowLeft: 'Nudge View Left',
                    ArrowRight: 'Nudge View Right',
                    ArrowUp: 'Nudge View Up',
                    Equal: 'Zoom In Tool',
                    KeyM: 'Pan Tool',
                    KeyR: 'ROI Tool',
                    KeyG: 'Grid Tool',
                    KeyQ: 'Go To Previous Image',
                    KeyW: 'Go To Next Image',
                    Minus: 'Zoom Out Tool',
                    NumpadAdd: 'Zoom In Tool',
                    NumpadSubtract: 'Zoom Out Tool'
                },
                ClassList: {
                    Enter: 'Add Class'
                },
                WorkspaceInput: {}
            },
            'Anomaly Recognition': {
                Workspace: {
                    ArrowDown: 'Nudge View Down',
                    ArrowLeft: 'Nudge View Left',
                    ArrowRight: 'Nudge View Right',
                    ArrowUp: 'Nudge View Up',
                    Equal: 'Zoom In Tool',
                    KeyM: 'Pan Tool',
                    KeyR: 'ROI Tool',
                    KeyG: 'Grid Tool',
                    KeyQ: 'Go To Previous Image',
                    KeyW: 'Go To Next Image',
                    Minus: 'Zoom Out Tool',
                    NumpadAdd: 'Zoom In Tool',
                    NumpadSubtract: 'Zoom Out Tool'
                },
                ClassList: {},
                WorkspaceInput: {}
            },
            Segmentation: {
                Workspace: {
                    ArrowDown: function () {
                        return _this.canvasProvider.getActivePolygons().length > 0
                            ? 'Nudge Polygons Down 1px'
                            : 'Nudge View Down';
                    },
                    ArrowLeft: function () {
                        return _this.canvasProvider.getActivePolygons().length > 0
                            ? 'Nudge Polygons Left 1px'
                            : 'Nudge View Left';
                    },
                    ArrowRight: function () {
                        return _this.canvasProvider.getActivePolygons().length > 0
                            ? 'Nudge Polygons Right 1px'
                            : 'Nudge View Right';
                    },
                    ArrowUp: function () {
                        return _this.canvasProvider.getActivePolygons().length > 0
                            ? 'Nudge Polygons Up 1px'
                            : 'Nudge View Up';
                    },
                    Backspace: 'Delete Polygon',
                    Delete: 'Delete Polygon',
                    Equal: 'Zoom In Tool',
                    KeyA: 'Addition Tool',
                    KeyB: 'Brush Tool',
                    KeyD: 'Subtraction Tool',
                    KeyE: 'Eraser Tool',
                    KeyF: 'Cut From Foreground Tool',
                    KeyG: 'Cut From Background Tool',
                    KeyM: 'Pan Tool',
                    KeyP: 'Pen Tool',
                    KeyQ: 'Go To Previous Image',
                    KeyR: 'Brightness Tool',
                    KeyS: 'Smart Fill Tool',
                    KeyT: 'Select Tool',
                    KeyW: 'Go To Next Image',
                    'metaKey+KeyA': 'Select All Polygons',
                    'metaKey+KeyC': 'Copy Polygons',
                    'metaKey+KeyV': 'Paste Polygons',
                    'metaKey+KeyZ': 'Undo Action',
                    'metaKey+shiftKey+KeyZ': 'Redo Action',
                    Minus: 'Zoom Out Tool',
                    'shiftKey+ArrowDown': 'Nudge Polygons Down 5px',
                    'shiftKey+ArrowLeft': 'Nudge Polygons Left 5px',
                    'shiftKey+ArrowRight': 'Nudge Polygons Right 5px',
                    'shiftKey+ArrowUp': 'Nudge Polygons Up 5px'
                },
                ClassSelector: {
                    Enter: 'Add Class',
                    ArrowDown: 'Scroll Class List Up',
                    ArrowUp: 'Scroll Class List Down'
                },
                ImageLibrary: {
                // Enter: 'Add Class'
                },
                WorkspaceInput: {},
                WorkspaceAnnotator: {}
            },
            'Object Detection': {
                Workspace: {
                    ArrowDown: function () {
                        return _this.canvasProvider.getActivePolygons().length > 0
                            ? 'Nudge Polygons Down 1px'
                            : 'Nudge View Down';
                    },
                    ArrowLeft: function () {
                        return _this.canvasProvider.getActivePolygons().length > 0
                            ? 'Nudge Polygons Left 1px'
                            : 'Nudge View Left';
                    },
                    ArrowRight: function () {
                        return _this.canvasProvider.getActivePolygons().length > 0
                            ? 'Nudge Polygons Right 1px'
                            : 'Nudge View Right';
                    },
                    ArrowUp: function () {
                        return _this.canvasProvider.getActivePolygons().length > 0
                            ? 'Nudge Polygons Up 1px'
                            : 'Nudge View Up';
                    },
                    Backspace: 'Delete Polygon',
                    Delete: 'Delete Polygon',
                    Equal: 'Zoom In Tool',
                    Escape: 'Cancel Action',
                    KeyB: 'Bounding Box Tool',
                    KeyC: 'Contrast Tool',
                    KeyG: 'Grid Tool',
                    KeyM: 'Pan Tool',
                    KeyQ: 'Go To Previous Image',
                    KeyR: 'Brightness Tool',
                    KeyT: 'Select Tool',
                    KeyW: 'Go To Next Image',
                    'metaKey+KeyA': 'Select All Polygons',
                    'metaKey+KeyC': 'Copy Polygons',
                    'metaKey+KeyV': 'Paste Polygons',
                    'metaKey+KeyZ': 'Undo Action',
                    'metaKey+shiftKey+KeyZ': 'Redo Action',
                    Minus: 'Zoom Out Tool',
                    'shiftKey+ArrowDown': 'Nudge Polygons Down 5px',
                    'shiftKey+ArrowLeft': 'Nudge Polygons Left 5px',
                    'shiftKey+ArrowRight': 'Nudge Polygons Right 5px',
                    'shiftKey+ArrowUp': 'Nudge Polygons Up 5px'
                },
                ClassList: {
                    Enter: 'Add Class'
                },
                WorkspaceInput: {},
                WorkspaceAnnotator: {}
            }
        };
        /* This object translates tool names into tool function calls.
         * Please note that this should only call higher level functions.
         */
        _this.toolDefinitions = {
            // NOTE: Tool definitions should be sorted alphabetically.
            'Add Class': function (event) {
                _this.events.publish(EventName.CustomHotKeyHandler, { tool: 'Add Class', event: event });
            },
            'Addition Tool': function () {
                _this.activeToolProvider.setActiveTool(TaggingTool.Addition);
            },
            'Bounding Box Tool': function () {
                _this.activeToolProvider.setActiveTool(TaggingTool.BoundingBox);
            },
            'Brightness Tool': function () {
                _this.activeToolProvider.setActiveTool(TaggingTool.Brightness);
            },
            'Brush Tool': function () {
                _this.activeToolProvider.setActiveTool(TaggingTool.Brush);
            },
            // Exit out of various detection workspace tools
            'Cancel Action': function () {
                switch (_this.activeToolProvider.getActiveTool()) {
                    case TaggingTool.BoundingBox:
                        _this.activeToolProvider.setActiveTool(TaggingTool.Select);
                        break;
                    case TaggingTool.Select:
                        _this.canvasProvider.clearActivePolygons();
                        break;
                    case TaggingTool.PredictionsOverlay:
                        _this.events.publish(EventName.CustomHotKeyHandler, { tool: 'Exit Predictions Overlay' });
                }
            },
            'Contrast Tool': function () {
                _this.activeToolProvider.setActiveTool(TaggingTool.Contrast);
            },
            'ROI Tool': function () {
                _this.activeToolProvider.setActiveTool(TaggingTool.RegionOfInterest);
            },
            'Grid Tool': function () {
                _this.activeToolProvider.setActiveTool(TaggingTool.Grid);
            },
            'Copy Polygons': function () {
                _this.canvasProvider.copySelectedPolygons();
            },
            'Cut From Foreground Tool': function () {
                _this.activeToolProvider.setActiveTool(TaggingTool.ForegroundCut);
            },
            'Cut From Background Tool': function () {
                _this.activeToolProvider.setActiveTool(TaggingTool.BackgroundCut);
            },
            'Delete Polygon': function () {
                _this.canvasProvider.deleteSelectedPolygons();
            },
            'Eraser Tool': function () {
                _this.activeToolProvider.setActiveTool(TaggingTool.Eraser);
            },
            'Go To Previous Image': function () {
                _this.events.publish(EventName.CustomHotKeyHandler, { tool: 'Go To Previous Image' });
            },
            'Go To Next Image': function () {
                _this.events.publish(EventName.CustomHotKeyHandler, { tool: 'Go To Next Image' });
            },
            'Nudge View Down': function () {
                _this.canvasProvider.nudgeView(0, 10);
            },
            'Nudge View Left': function () {
                _this.canvasProvider.nudgeView(-10, 0);
            },
            'Nudge View Right': function () {
                _this.canvasProvider.nudgeView(10, 0);
            },
            'Nudge View Up': function () {
                _this.canvasProvider.nudgeView(0, -10);
            },
            'Nudge Polygons Down 1px': function () {
                _this.canvasProvider.nudgeSelectedPolygons('down', 1);
            },
            'Nudge Polygons Left 1px': function () {
                _this.canvasProvider.nudgeSelectedPolygons('left', 1);
            },
            'Nudge Polygons Right 1px': function () {
                _this.canvasProvider.nudgeSelectedPolygons('right', 1);
            },
            'Nudge Polygons Up 1px': function () {
                _this.canvasProvider.nudgeSelectedPolygons('up', 1);
            },
            'Nudge Polygons Down 5px': function () {
                _this.canvasProvider.nudgeSelectedPolygons('down', 5);
            },
            'Nudge Polygons Left 5px': function () {
                _this.canvasProvider.nudgeSelectedPolygons('left', 5);
            },
            'Nudge Polygons Right 5px': function () {
                _this.canvasProvider.nudgeSelectedPolygons('right', 5);
            },
            'Nudge Polygons Up 5px': function () {
                _this.canvasProvider.nudgeSelectedPolygons('up', 5);
            },
            'Pan Tool': function () {
                _this.activeToolProvider.setActiveTool(TaggingTool.Pan);
            },
            'Paste Polygons': function () {
                if (_this.canvasProvider.getClipboardPolygons().length > 0) {
                    _this.canvasProvider.pasteCurrentPolygons(_this.getCurrentWorkspaceType());
                    _this.activeToolProvider.setActiveTool(TaggingTool.Select);
                }
            },
            'Pen Tool': function () {
                _this.activeToolProvider.setActiveTool(TaggingTool.Pen);
            },
            'Redo Action': function () {
                _this.canvasProvider.redo();
            },
            'Select All Polygons': function () {
                _this.canvasProvider.selectAllPolygons();
                var allPolygons = _this.canvasProvider.getAllPolygons();
            },
            'Select Tool': function () {
                _this.activeToolProvider.setActiveTool(TaggingTool.Select);
            },
            'Smart Fill Tool': function () {
                _this.activeToolProvider.setActiveTool(TaggingTool.SmartFill);
            },
            'Subtraction Tool': function () {
                if (_this.canvasProvider.canSubtractPolygons()) {
                    _this.activeToolProvider.setActiveTool(TaggingTool.Subtraction);
                }
            },
            'Undo Action': function () {
                _this.canvasProvider.undo();
            },
            'Zoom In Tool': function () {
                _this.activeToolProvider.setActiveTool(TaggingTool.ZoomIn);
            },
            'Zoom Out Tool': function () {
                _this.activeToolProvider.setActiveTool(TaggingTool.ZoomOut);
            }
        };
        _this.modifierKeysCurrentlyPressed = {
            altKey: false,
            metaKey: false,
            shiftKey: false
        };
        return _this;
    }
    WorkspaceProvider.prototype.getCurrentWorkspaceType = function () {
        return this.currentWorkspaceType;
    };
    WorkspaceProvider.prototype.setCurrentWorkspaceType = function (workspace) {
        this.currentWorkspaceType = workspace;
    };
    /** Return information about the subcomponent that's currently hooking user key input.
     */
    WorkspaceProvider.prototype.getHotKeysMode = function () {
        return this.currentHotKeysMode;
    };
    /** Update our representation of what subcomponent the user has open.
     * It is up to developers to call this when they want
     * to change which part of a workspace is hooking user key input.
     * @param mode The developer's desired hotkey mode.
     */
    WorkspaceProvider.prototype.setHotKeysMode = function (mode) {
        this.currentHotKeysMode = mode;
    };
    WorkspaceProvider.prototype.hotKeysDown = function (event) {
        if (!this.currentWorkspaceType || !this.currentHotKeysMode) {
            return;
        }
        // If the user explicitly hits a "modifier" key, we need to track that.
        if (event.code === 'AltLeft' || event.code === 'AltRight') {
            this.modifierKeysCurrentlyPressed.altKey = true;
        }
        if (event.code === 'CtrlLeft' ||
            event.code === 'CtrlRight' ||
            event.code === 'MetaLeft' ||
            event.code === 'MetaRight') {
            this.modifierKeysCurrentlyPressed.metaKey = true;
        }
        if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') {
            this.modifierKeysCurrentlyPressed.shiftKey = true;
            this.canvasProvider.setShiftIsDown(true); // Stopgap, until we have a more robust hotkey provider
        }
        // Parse the key event step by step, and run any corresponding tool.
        // Please note that you can't pass your own arguments to the tools with this architecture.
        // We can, however, toss in the hardcoded event.
        try {
            var hotKeyHash = this.buildHash(event);
            var tool = this.findTool(hotKeyHash);
            if (tool && this.toolDefinitions[tool]) {
                this.toolDefinitions[tool](event);
            }
        }
        catch (error) {
            console.error('Parsing hotkey failed!\n', error);
        }
    };
    WorkspaceProvider.prototype.hotKeysUp = function (event) {
        // When the user stops pressing a modifier, update our state representation.
        if (event.code === 'AltLeft' || event.code === 'AltRight') {
            this.modifierKeysCurrentlyPressed.altKey = false;
        }
        if (event.code === 'CtrlLeft' ||
            event.code === 'CtrlRight' ||
            event.code === 'MetaLeft' ||
            event.code === 'MetaRight') {
            this.modifierKeysCurrentlyPressed.metaKey = false;
        }
        if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') {
            this.modifierKeysCurrentlyPressed.shiftKey = false;
            this.canvasProvider.setShiftIsDown(false);
        }
    };
    /** Given a key combination, translate it into a standardized string, so we can find the appropriate tool.
     * Note that we translate the 'Ctrl' and 'Meta' keys to ensure cross-platform compatibility.
     * @param event The Angular keydown event passed from the root workspace component template.
     */
    WorkspaceProvider.prototype.buildHash = function (event) {
        var hash = '';
        if (event.altKey) {
            hash += 'altKey+';
        }
        if (event.metaKey || event.ctrlKey) {
            hash += 'metaKey+';
        }
        if (event.shiftKey) {
            hash += 'shiftKey+';
        }
        hash += event.code;
        return hash;
    };
    /** Convert a hash into a tool definition.
     * @param hash The standardized hotkey combination name returned by buildHash().
     */
    WorkspaceProvider.prototype.findTool = function (hash) {
        // Drill into our definitions one step at a time, in order to make debugging easier.
        var currentWorkspaceDefinitions = this.hotKeyDefinitions[this.getCurrentWorkspaceType()];
        if (!currentWorkspaceDefinitions) {
            console.error('No hotkey definition for workspace', this.getCurrentWorkspaceType());
            return null;
        }
        var currentHotKeysModeDefinitions = currentWorkspaceDefinitions[this.getHotKeysMode()];
        if (!currentHotKeysModeDefinitions) {
            console.error('No hotkey definition for hotkeys mode', this.getHotKeysMode());
            return null;
        }
        // In order to support more complicated state tracking for tool selection, we need to
        // add support for evaluating a definition key as a function. For example, this lets us
        // situationally activate polygon nudging logic when the user has polygons seleted and
        // presses certain hotkeys.
        return currentHotKeysModeDefinitions[hash] instanceof Function
            ? currentHotKeysModeDefinitions[hash]()
            : currentHotKeysModeDefinitions[hash];
    };
    /*****************************
     *      Common Functions     *
     ****************************/
    /* TODO: Figure out how to handle functions that aren't bound to hotkeys. An example follows.
       It looks like at least some of them can simply be migrated into this provider.
       We should make a special effort to ensure the functions are properly organized.
    */
    // These functions are currently only used for recognition (classifier and anomaly recognizer)
    WorkspaceProvider.prototype.getSaveAndMoveButtonText = function (rootPage) {
        return rootPage === WorkspaceRootPage.EVALUATE_BRAIN
            ? this.translate.instant('Save & Close')
            : this.translate.instant('Save & Continue');
    };
    WorkspaceProvider.prototype.getSaveAndMoveButtonTooltip = function (rootPage) {
        return "Save your selection and " + (rootPage === WorkspaceRootPage.EVALUATE_BRAIN
            ? 'return to the validation image details view'
            : 'move to the next image');
    };
    /**
     * Return true if it is appropriate to load asset predictions for the current dataset given
     * the current workspace root page
     *
     * @param dataset
     * @param rootPage The workspace root page from which the current view was launched
     */
    WorkspaceProvider.prototype.shouldLoadPredictionsForAssets = function (dataset, rootPage) {
        return rootPage === WorkspaceRootPage.EVALUATE_BRAIN || this.datasetProvider.getLearnAsYouGo(dataset);
    };
    /**
     * Return true if it is appropriate to run incremental training for the given dataset
     * and workspace root page
     *
     * @param dataset
     * @param rootPage The workspace root page from which the current view was launched
     */
    WorkspaceProvider.prototype.shouldRunIncrementalTraining = function (dataset, rootPage) {
        return rootPage === WorkspaceRootPage.WORKSPACE && this.datasetProvider.getLearnAsYouGo(dataset);
    };
    WorkspaceProvider = __decorate([
        Injectable(),
        __metadata("design:paramtypes", [CanvasProvider,
            ActiveToolProvider,
            Events,
            DatasetProvider,
            TranslateService])
    ], WorkspaceProvider);
    return WorkspaceProvider;
}(BaseProvider));
export { WorkspaceProvider };
//# sourceMappingURL=workspace.js.map


// WEBPACK FOOTER //
// ./src/providers/workspace.ts.pre-build-optimizer.js